<template>
  <div class="auth-wrapper auth-v2">
    <b-row class="auth-inner m-0">
      <b-link class="brand-logo d-none d-md-flex">
        <logo />

        <h2 class="brand-text text-primary ml-1">{{ appName }}</h2>
      </b-link>

      <b-navbar-nav
        class="nav align-items-center ml-auto absolute-locale d-none d-md-flex"
      >
        <locale></locale>
      </b-navbar-nav>

      <b-col lg="8" class="d-none d-lg-flex align-items-center p-5">
        <div
          class="w-100 d-lg-flex align-items-center justify-content-center px-5"
        >
          <b-img fluid :src="imgUrl" />
        </div>
      </b-col>

      <b-col lg="4" class="d-flex align-items-center auth-bg px-2 p-lg-5">
        <b-col sm="8" md="6" lg="12" class="px-xl-2 mx-auto">
          <ul class="no-bullet">
            <locale class="d-md-none"></locale>
          </ul>
          <div v-if="isValid">
            <b-card-title title-tag="h2" class="font-weight-bold mb-1">
              {{ $t("breadcrumb.welcome") }}
            </b-card-title>
            <b-card-text class="mb-2" v-if="step == 1">
              {{ $t("general.needBasicInformation") }}
            </b-card-text>
            <b-card-text class="mb-2" v-else>
              {{ $t("general.whatKindOfbusiness") }}
            </b-card-text>

            <validation-observer ref="registerForm" #default="{ invalid }">
              <b-form
                class="auth-register-form mt-2"
                @submit.prevent="validationForm"
              >
                <n-input :fields="fields" v-model="data"> </n-input>

                <n-button-loading
                  type="submit"
                  variant="primary"
                  :loading="loading"
                  :disabled="invalid"
                  block
                >
                  <span v-if="step == 1">
                    {{ $t("button.signUp") }}
                  </span>
                  <span v-else>
                    {{ $t("button.finish") }}
                  </span>
                </n-button-loading>
              </b-form>
            </validation-observer>

            <p class="text-center mt-2">
              <span>{{ $t("general.alreadyHaveAccount") }}</span>
              <b-link class="link-underline" :to="{ name: 'login' }">
                <span>&nbsp;{{ $t("button.signIn") }}</span>
              </b-link>
            </p>
          </div>
          <div class="text-center" v-else>
            <b-img
              fluid
              :src="require('@/assets/images/icons/close.png')"
              class="w-25 mb-2"
            ></b-img>
            <b-card-title
              title-tag="h2"
              class="text-center font-weight-bold mb-1"
            >
              {{ $t("invitation.linkExpired") }}
            </b-card-title>
          </div>
        </b-col>
      </b-col>
    </b-row>
  </div>
</template>

<script>
/* eslint-disable global-require */
import Logo from "@core/layouts/components/Logo.vue";
import {
  BRow,
  BCol,
  BLink,
  BButton,
  BForm,
  BFormGroup,
  BFormCheckbox,
  BImg,
  BCardTitle,
  BCardText,
  BNavbarNav,
} from "bootstrap-vue";
import store from "@/store/index";
import { $themeConfig } from "@themeConfig";
import FormInput from "./registerBasicFormInput";
import NInput from "@/components/NInput";
import NButtonLoading from "@/components/NButtonLoading";
import Locale from "@/layouts/components/app-navbar/Locale.vue";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import useJwt from "@/auth/jwt/useJwt";
import { getHomeRouteForLoggedInUser } from "@/auth/utils";
import { initialAbility } from "@/libs/acl/config";
import Repository from "@/repositories/RepositoryFactory";
import { mapValidationMessage, mapAbility } from "@/libs/helper";

const RegisterRepository = Repository.get("register");

export default {
  components: {
    BRow,
    BImg,
    BCol,
    BLink,
    BButton,
    BForm,
    BFormGroup,
    BCardText,
    BCardTitle,
    BFormCheckbox,
    BNavbarNav,
    Logo,
    Locale,
    NInput,
    NButtonLoading,
  },
  data() {
    return {
      data: {
        firstName: null,
        lastName: null,
        phone: this.$route.query.phone || null,
        password: null,
        retypePassword: null,
      },
      step: 1,
      loading: false,
      isValid: true,
      sideImg: require("@/assets/images/pages/register-v2.svg"),
    };
  },
  computed: {
    imgUrl() {
      if (store.state.appConfig.layout.skin === "dark") {
        // eslint-disable-next-line vue/no-side-effects-in-computed-properties
        this.sideImg = require("@/assets/images/pages/register-v2-dark.svg");
        return this.sideImg;
      }
      return this.sideImg;
    },
    token() {
      return this.$route.query.token;
    },
  },
  beforeMount() {
    if (!this.token) {
      this.$router.replace({ name: "login" });
    } else {
      this.checkToken();
    }
  },
  methods: {
    checkToken() {
      this.loading = true;
      RegisterRepository.verifyInvitation({ token: this.token })
        .then((response) => {
          const data = response?.data?.data;
          if (data) {
            this.isValid = data.isValid;
          }
        })
        .catch((error) => {})
        .then(() => {
          this.loading = false;
        });
    },
    validationForm() {
      this.$refs.registerForm.validate().then((success) => {
        if (success) {
          this.submit();
        }
      });
    },
    submit() {
      this.loading = true;

      RegisterRepository.registerInvitation({
        ...this.data,
        token: this.token,
        origin: Intl
          ? Intl.DateTimeFormat().resolvedOptions().timeZone
          : "Asia/Phnom_Penh",
      })
        .then((response) => {
          const data = response?.data?.data;

          const userData = data.user || {};
          useJwt.setToken(data.accessToken);
          useJwt.setRefreshToken(data.refreshToken);
          localStorage.setItem(useJwt.jwtConfig.storageUserDataKeyName, JSON.stringify(userData));
          this.$store.commit("profile/UPDATE_PROFILE", userData);

          let newAbility = [...initialAbility];
          if (userData?.ability) {
            newAbility = mapAbility(userData, newAbility, initialAbility);
          }
          this.$ability.update(newAbility);
          this.$router
            .replace(getHomeRouteForLoggedInUser("admin", null, newAbility))
            .then(() => {
              this.$toast({
                component: ToastificationContent,
                position: "top-right",
                props: {
                  title: this.$t("general.welcomeUser", {
                    name: this.data.firstName,
                  }),
                  icon: "CoffeeIcon",
                  variant: "success",
                  text: this.$t("alert.successRegistered"),
                },
              });
            });
        })
        .catch((error) => {
          if (error?.response?.status == 422) {
            const errorMessage = mapValidationMessage(
              error.response.data.validate
            );
            this.$refs.registerForm.setErrors(errorMessage);
          }
        })
        .then(() => {
          this.loading = false;
        });
    },
  },
  setup() {
    const { appName } = $themeConfig.app;
    const fields = FormInput;
    return {
      appName,
      fields,
    };
  },
};
/* eslint-disable global-require */
</script>

<style lang="scss">
@import "@core/scss/vue/pages/page-auth.scss";
</style>
